<template>
  <div class="ai-shell">
    <tracey-ai-sidebar
      ref="sidebar"
      :threads="filteredThreads"
      :active-thread-id="activeThreadId"
      :thread-search-input.sync="threadSearchInput"
      :is-threads-loading="isThreadsLoading"
      :thread-list-height="threadListHeight"
      :collapsed="sidebarCollapsed"
      :is-thread-busy="isThreadBusy"
      @new-chat="startNewChat"
      @select-thread="selectThread"
      @delete-thread="deleteThread"
      @feedback="sendProductFeedback"
    />

    <section class="ai-main">
      <header class="ai-main__topbar">
        <v-btn
          icon
          class="ai-main__sidebar-toggle"
          :aria-label="sidebarCollapsed ? 'Open thread list' : 'Collapse thread list'"
          @click="sidebarCollapsed = !sidebarCollapsed"
        >
          <v-icon color="#353740">{{ sidebarCollapsed ? 'mdi-menu' : 'mdi-chevron-left' }}</v-icon>
        </v-btn>
        <h1 class="ai-main__title">{{ chatTitle }}</h1>
        <v-btn
          small
          outlined
          class="ai-main__tutorial-btn"
          data-tutorial="tutorial-btn"
          aria-label="Start the Tracey walkthrough"
          title="Take the Tracey walkthrough"
          @click="startTutorial"
        >
          <v-icon left small>mdi-lightbulb-on-outline</v-icon>
          <span class="ai-main__btn-label">Tutorial</span>
        </v-btn>
        <div v-if="showCreditsChip" class="ai-main__credits-group" data-tutorial="usage">
          <div
            v-for="chip in creditsChips.filter((c) => c.loaded)"
            :key="chip.key"
            class="ai-main__credits"
            :class="{
              [`ai-main__credits--${chip.key}`]: true,
              'ai-main__credits--empty': chip.empty,
              'ai-main__credits--low': chip.low && !chip.empty,
            }"
            :title="chip.tooltip"
          >
            <v-icon x-small class="ai-main__credits-icon">mdi-lightning-bolt</v-icon>
            <span class="ai-main__credits-agent">{{ chip.key === 'ultra' ? 'Ultra' : 'Sprint' }}</span>
            <span class="ai-main__credits-text">{{ chip.label }}</span>
          </div>
          <v-btn
            small
            depressed
            class="ai-main__topup-btn"
            aria-label="Top up Tracey credits"
            title="Top up Tracey credits"
            @click="showTopUpModal = true"
          >
            <v-icon left small color="#fff">mdi-plus-circle-outline</v-icon>
            <span class="ai-main__btn-label">Top up</span>
          </v-btn>
          <v-btn
            small
            outlined
            class="ai-main__usage-btn"
            aria-label="View Tracey usage"
            title="View Tracey usage"
            @click="$router.push('/usage')"
          >
            <v-icon left small>mdi-chart-bar</v-icon>
            <span class="ai-main__btn-label">Usage</span>
          </v-btn>
        </div>
      </header>

      <tracey-credits-topup-modal v-model="showTopUpModal" :default-agent="traceyVersion" />

      <div class="ai-main__body">
        <div v-if="isChatLoading" class="ai-main__loading-skeletons" aria-label="Loading conversation">
          <div v-for="n in 3" :key="n" class="ai-main__skeleton-turn">
            <v-skeleton-loader type="list-item-avatar-two-line" />
          </div>
        </div>

        <div class="ai-main__feed-wrap">
        <div
          ref="chatContainer"
          class="ai-main__feed"
          :class="{ 'ai-main__feed--empty': showEmptyHero }"
          @scroll.passive="onFeedScroll"
        >
        <tracey-ai-empty-hero
          v-if="showEmptyHero"
          :agent-key="traceyVersion"
          @suggestion="applySuggestion"
        />

        <template v-else-if="activeThread && activeThread.version === 'v1'">
          <tracey-ai-v1-turn
            v-for="(message, idx) in activeThreadMessages"
            :key="`v1-${idx}`"
            :message="message"
            :is-query-data-present="isQueryDataPresent"
            :format-timestamp="formatTimestamp"
            @view-data="viewQueryData"
          />
        </template>

        <template v-else>
          <tracey-ai-message-row
            v-for="(msg, idx) in displayMessages"
            :key="`msg-${msg.id || idx}`"
            :message="msg"
            :data-query-id="msg.id"
            :is-query-data-present="isQueryDataPresent"
            :format-timestamp="formatTimestamp"
            @view-data="viewQueryData(msg)"
            @retry="retryMessage(msg)"
          />
          <tracey-ai-streaming-row
            v-if="isStreamingInActiveThread"
            :thinking="activeThreadStreamingState.thinking"
            :content="activeThreadStreamingState.content"
            :pending-tools="activeThreadStreamingState.pendingTools"
            :all-tool-calls="activeThreadStreamingState.allToolCalls"
          />
          <tracey-ai-streaming-row
            v-else-if="submittingThreadIds[activeThreadId] && !streamingByThreadId[activeThreadId]"
          />
          <tracey-ai-streaming-row
            v-else-if="awaitingReplyThreadIds[activeThreadId]"
          />
        </template>
        </div>

        <v-btn
          v-show="!isAtBottom && !showEmptyHero"
          fab
          x-small
          class="ai-main__jump-bottom"
          aria-label="Scroll to latest"
          @click="scrollToBottom(true)"
        >
          <v-icon color="#fff">mdi-chevron-down</v-icon>
        </v-btn>

        <tracey-ai-query-nav
          v-if="userQueries.length > 1"
          :queries="userQueries"
          @navigate="scrollToQuery"
        />
        </div>

        <tracey-ai-composer
          ref="composer"
          v-model="inputText"
          :is-busy="isActiveThreadBusy"
          :show-prompt-library="showPromptLibrary"
          :show-deprecated-overlay="activeThread && activeThread.version === 'v1'"
          :out-of-credits="outOfCredits"
          :reset-label="creditsResetLabel"
          :tracey-version="traceyVersion"
          @update:tracey-version="traceyVersion = $event"
          @submit="submitMessage"
          @stop="stopGeneration"
          @toggle-prompt-library="togglePromptLibrary"
          @open-topup="showTopUpModal = true"
        />
      </div>
    </section>

    <tracey-ai-prompt-panel
      v-if="showPromptLibrary"
      @close="togglePromptLibrary"
      @select="usePrompt"
    />

    <!-- Tool call selection modal -->
    <v-dialog v-model="showToolCallModal" max-width="600" persistent>
      <v-card>
        <v-card-title class="headline">Select Query Data to View</v-card-title>
        <v-card-text>
          <p class="mb-4">Multiple queries were found. Please select which one you'd like to view:</p>
          <v-list>
            <v-list-item v-for="(toolCall, index) in matchingToolCalls" :key="index"
              @click="selectToolCallAndView(toolCall)" class="mb-2"
              style="border: 1px solid #e0e0e0; border-radius: 4px; cursor: pointer;">
              <v-list-item-content>
                <v-list-item-title>{{ getToolCallDisplayName(toolCall.name) }}</v-list-item-title>
                <v-list-item-subtitle v-if="getToolCallDisplaySummary(toolCall)" class="tool-call-summary">
                  {{ getToolCallDisplaySummary(toolCall) }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon>mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showToolCallModal = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <tracey-tutorial
      v-model="showTutorial"
      :steps="tutorialSteps"
      @step="onTutorialStep"
      @finish="onTutorialFinish"
    />
  </div>
</template>

<script>
import { marked } from 'marked';
import {
  submitTraceyQuery,
  fetchUserThreadsAPI,
  fetchMessagesForThreadAPI,
  deleteTraceyThread,
  stopTraceyThread,
} from '../APIManager.js';
import creditsMixin from './creditsMixin.js';

// How often, and for how long, to check for a reply that is still being
// produced after the page was left mid-answer (5s for up to 10 minutes).
const REPLY_POLL_INTERVAL_MS = 5000;
const REPLY_POLL_MAX_ATTEMPTS = 120;
import TraceyAiSidebar from './page/TraceyAiSidebar.vue';
import TraceyAiMessageRow from './page/TraceyAiMessageRow.vue';
import TraceyAiStreamingRow from './page/TraceyAiStreamingRow.vue';
import TraceyAiV1Turn from './page/TraceyAiV1Turn.vue';
import TraceyAiComposer from './page/TraceyAiComposer.vue';
import TraceyAiPromptPanel from './page/TraceyAiPromptPanel.vue';
import TraceyAiEmptyHero from './page/TraceyAiEmptyHero.vue';
import TraceyAiQueryNav from './page/TraceyAiQueryNav.vue';
import TraceyTutorial from './page/TraceyTutorial.vue';
import TraceyCreditsTopupModal from './TraceyCreditsTopupModal.vue';

const TUTORIAL_SEEN_KEY = 'tt_tracey_tutorial_seen';

// Below this viewport width (CSS px — shrinks as the user zooms in) the thread
// sidebar auto-collapses so the conversation column keeps usable space.
const SIDEBAR_AUTO_COLLAPSE_WIDTH = 1024;

const QUERY_DATA_TOOLS = ['get_leaderboard', 'search_tenders', 'get_market_insights', 'get_search_summary'];

const TOOL_TO_VIEW_INDEX = {
  get_leaderboard: 1,
  search_tenders: 0,
  get_market_insights: 3,
  get_search_summary: 0,
};

export default {
  name: 'TraceyPageChat',
  mixins: [creditsMixin],
  components: {
    TraceyAiSidebar,
    TraceyAiMessageRow,
    TraceyAiStreamingRow,
    TraceyAiV1Turn,
    TraceyAiComposer,
    TraceyAiPromptPanel,
    TraceyAiEmptyHero,
    TraceyAiQueryNav,
    TraceyTutorial,
    TraceyCreditsTopupModal,
  },
  data() {
    return {
      isComponentDestroyed: false,
      /**
       * False while the page is cached in the background (App.vue keeps it
       * alive so in-flight answers keep streaming when the user visits another
       * page). URL watchers must not act then, or they would reset the chat or
       * navigate the user back here.
       */
      isPageActive: true,
      hasBeenActivated: false,
      sidebarCollapsed: false,
      // Viewport width state for zoom/resize-aware sidebar collapsing.
      viewportNarrow: false,
      sidebarAutoCollapsed: false,
      threadListHeight: 400,
      threadListResizeObserver: null,
      // Whether the chat feed is scrolled to (near) the bottom. Drives "smart"
      // auto-scroll — we only stick to the bottom while streaming if the user is
      // already there, and show a jump-to-bottom button otherwise.
      isAtBottom: true,
      inputText: '',
      // "sprint" | "ultra" -- which Tracey agent the NEXT query goes to. Both
      // agents share the same thread_id server-side, so switching mid-thread
      // still sees the full prior transcript regardless of which agent wrote it.
      traceyVersion: 'ultra',
      showTopUpModal: false,
      threads: [],
      activeThread: null,
      activeThreadId: null,
      /** @type {Record<string, Array>} Messages keyed by thread ID */
      messagesByThreadId: {},
      threadSearchInput: '',
      isChatLoading: false,
      isThreadsLoading: false,
      /** Per-thread: thread IDs that sent a request but stream not started yet */
      submittingThreadIds: {},
      /** Per-thread: { content, pendingTools, allToolCalls } for active streams */
      streamingByThreadId: {},
      /** Per-thread: AbortController for each in-flight request */
      streamAbortControllers: {},
      /**
       * Per-thread: true while an opened thread ends in an unanswered query
       * whose run is still finishing server-side (the page was left or reloaded
       * mid-answer), so we poll history until the reply lands.
       */
      awaitingReplyThreadIds: {},
      replyPollTimers: {},
      showToolCallModal: false,
      toolCallModalMessage: null,
      matchingToolCalls: [],
      showPromptLibrary: false,
      showTutorial: false,
      tutorialSteps: [
        {
          key: 'agent-select',
          selector: '.tracey-agent-select__activator',
          title: 'Choose your agent',
          body: [
            'Start by choosing the Tracey agent that best suits what you need.',
            'Tracey Ultra is built for deeper strategic analysis, opportunity planning and evidence-backed recommendations across contracts, budgets, policy and market data.',
            'For quick answers, searches and procurement insights, Tracey Sprint gives you fast, focused intelligence.',
          ],
          hint: 'Click here to open the menu and select an agent.',
          placement: 'top',
        },
        {
          key: 'composer',
          selector: '[data-tutorial="composer"]',
          title: 'Ask a question',
          body: [
            'Once you have selected your agent, simply ask Tracey a question to start exploring government tenders, procurement data and budget intelligence.',
          ],
          placement: 'top',
        },
        {
          key: 'history',
          selector: '[data-tutorial="history"]',
          title: 'Prompt history',
          body: [
            'Your previous prompts are saved here, so you can easily revisit your research and continue where you left off.',
          ],
          placement: 'right',
        },
        {
          key: 'usage',
          selector: '[data-tutorial="usage"]',
          title: 'Check your usage',
          body: [
            'Keep an eye on your Tracey usage and available credits here, and top up when needed.',
          ],
          placement: 'bottom',
        },
        {
          key: 'profile',
          selector: '[data-tutorial="profile"]',
          title: 'Give Tracey context',
          body: [
            'For more relevant and meaningful insights, make sure Tracey understands your business context.',
            'Head to your Tendertrace profile and make sure your profile information is properly set up.',
          ],
          placement: 'bottom',
        },
        {
          key: 'tutorial-btn',
          selector: '[data-tutorial="tutorial-btn"]',
          title: 'Need a refresher?',
          body: [
            'You can find this Tracey walkthrough here anytime, so you can always get the most out of your Tracey experience.',
          ],
          placement: 'bottom',
        },
      ],
    };
  },
  computed: {
    filteredThreads() {
      if (!this.threadSearchInput?.trim()) return this.threads;
      const term = this.threadSearchInput.toLowerCase().trim();
      return this.threads.filter(
        (t) => (t.title && t.title.toLowerCase().includes(term)) || (t.id && String(t.id).toLowerCase().includes(term))
      );
    },
    activeThreadMessages() {
      if (!this.activeThreadId) return [];
      return this.messagesByThreadId[this.activeThreadId] ?? [];
    },
    /** v2 messages only; excludes v1 format */
    displayMessages() {
      const msgs = this.activeThreadMessages;
      return msgs.filter((m) => m.version !== 'v1' && (m.role === 'user' || m.role === 'assistant'));
    },
    /** User queries in the active chat, for the query navigator. */
    userQueries() {
      return this.displayMessages
        .filter((m) => m.role === 'user')
        .map((m) => ({ id: m.id, text: m.content || '' }));
    },
    /** Streaming state for the currently visible thread (for template) */
    activeThreadStreamingState() {
      if (!this.activeThreadId) return null;
      return this.streamingByThreadId[this.activeThreadId] || null;
    },
    isStreamingInActiveThread() {
      return !!this.activeThreadStreamingState;
    },
    /** True if the active thread has a request in flight or is streaming */
    isActiveThreadBusy() {
      if (!this.activeThreadId) return false;
      return !!(
        this.submittingThreadIds[this.activeThreadId]
        || this.streamingByThreadId[this.activeThreadId]
        || this.awaitingReplyThreadIds[this.activeThreadId]
      );
    },
    chatTitle() {
      if (!this.activeThreadId) return 'New chat';
      return this.activeThread?.title || 'Untitled';
    },
    showEmptyHero() {
      if (this.isChatLoading) return false;
      if (this.activeThread?.version === 'v1') return false;
      if (this.isStreamingInActiveThread) return false;
      if (this.activeThreadId && this.submittingThreadIds[this.activeThreadId]) return false;
      return this.displayMessages.length === 0;
    },
  },
  watch: {
    activeThreadMessages: { handler() { this.scrollToBottom(); }, deep: true },
    activeThreadStreamingState: { handler() { this.scrollToBottom(); }, deep: true },
    // Keep the URL in sync with the open thread so a reload restores it. 'pending'
    // is the transient key before a new chat gets its real server thread id.
    activeThreadId(id) {
      if (!this.isPageActive) return;
      const routeId = this.$route.params.threadId || '';
      if (id == null) {
        if (routeId) this.$router.replace({ name: 'tracey-chat' }).catch(() => {});
        return;
      }
      if (id === 'pending') return;
      if (String(id) !== String(routeId)) {
        this.$router.replace({ name: 'tracey-chat', params: { threadId: String(id) } }).catch(() => {});
      }
    },
    // React to external URL changes (reload, back/forward, "Ask Tracey").
    '$route.params.threadId'(id) {
      if (!this.isPageActive || this.$route.name !== 'tracey-chat') return;
      const current = this.activeThreadId != null ? String(this.activeThreadId) : '';
      if (!id) {
        if (current && current !== 'pending') this.startNewChat();
        return;
      }
      if (String(id) === current) return;
      const thread = this.threads.find((t) => String(t.id) === String(id))
        || { id, version: 'v2', title: 'Untitled' };
      this.selectThread(thread);
    },
    traceyVersion(version) {
      // Sprint and Ultra each have their own wallet, so switching agents
      // means showing a different balance, not just relabelling the chip.
      this.loadCredits(version);
    },
  },
  async mounted() {
    this.onViewportResize();
    window.addEventListener('resize', this.onViewportResize, { passive: true });
    const threadsReady = this.fetchThreads();
    this.loadAllCredits();
    this.$nextTick(() => this.initThreadListResizeObserver());
    // Restore the thread named in the URL (deep link / reload) once threads load.
    try {
      await threadsReady;
    } catch (e) {
      /* thread list failed to load — restore is best-effort */
    }
    this.restoreThreadFromRoute();
    this.maybeAutoStartTutorial();
  },
  activated() {
    // Vue also calls this right after the first mount; mounted() handles that.
    if (!this.hasBeenActivated) {
      this.hasBeenActivated = true;
      return;
    }
    this.isPageActive = true;
    this.syncWithRouteOnReturn();
    this.loadAllCredits();
  },
  deactivated() {
    this.isPageActive = false;
    // The tour anchors to on-screen elements; don't leave it mid-step.
    this.showTutorial = false;
  },
  beforeDestroy() {
    this.isComponentDestroyed = true;
    window.removeEventListener('resize', this.onViewportResize);
    if (this.threadListResizeObserver) {
      this.threadListResizeObserver.disconnect();
      this.threadListResizeObserver = null;
    }
    // Do not abort in-flight streams; they continue in the background.
    // User must explicitly stop a thread via the Stop button.
    Object.keys(this.replyPollTimers).forEach((id) => clearInterval(this.replyPollTimers[id]));
  },
  methods: {
    /**
     * Collapse the thread sidebar when the viewport becomes too narrow for it
     * (browser zoom changes the viewport's CSS width and fires resize), and
     * restore it when there's room again. Acts only on threshold crossings, so
     * a manual toggle while narrow is respected rather than fought.
     */
    onViewportResize() {
      if (typeof window === 'undefined') return;
      const narrow = window.innerWidth < SIDEBAR_AUTO_COLLAPSE_WIDTH;
      if (narrow && !this.viewportNarrow && !this.sidebarCollapsed) {
        this.sidebarCollapsed = true;
        this.sidebarAutoCollapsed = true;
      } else if (!narrow && this.viewportNarrow && this.sidebarAutoCollapsed) {
        this.sidebarCollapsed = false;
        this.sidebarAutoCollapsed = false;
      }
      this.viewportNarrow = narrow;
    },

    initThreadListResizeObserver() {
      const el = this.$refs.sidebar?.$refs?.threadListViewport;
      if (!el || typeof ResizeObserver === 'undefined') return;
      const update = () => {
        this.threadListHeight = Math.max(200, el.clientHeight);
      };
      update();
      this.threadListResizeObserver = new ResizeObserver(update);
      this.threadListResizeObserver.observe(el);
    },

    parseMarkdown(text) {
      return text ? marked.parse(text) : '';
    },
    formatTimestamp(ts) {
      if (!ts) return 'Invalid Date';
      const normalized = /([Z]|[+-]\d{2}:\d{2})$/.test(ts) ? ts : ts + 'Z';
      const date = new Date(normalized);
      if (isNaN(date)) return 'Invalid Date';
      return date.toLocaleString(undefined, {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    },
    autoGrowTextarea() {
      this.$refs.composer?.autoGrow?.();
    },

    applySuggestion(text) {
      this.inputText = text;
      this.$nextTick(() => this.$refs.composer?.focusInput?.());
    },

    sendProductFeedback() {
      window.location.href =
        'mailto:' +
        'support@tendertrace.com?subject=Tendertrace Product Feedback&body=Product Feature: Tracey%0D%0DPlease enter your message below this line.%0D';
    },
    scrollToBottom(force = false) {
      this.$nextTick(() => {
        const el = this.$refs.chatContainer;
        if (!el) return;
        if (force || this.isAtBottom) {
          el.scrollTop = el.scrollHeight;
          this.isAtBottom = true;
        }
      });
    },

    onFeedScroll() {
      const el = this.$refs.chatContainer;
      if (!el) return;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      this.isAtBottom = distanceFromBottom < 120;
    },

    scrollToQuery(id) {
      const container = this.$refs.chatContainer;
      if (!container || id == null) return;
      const el = container.querySelector(`[data-query-id="${String(id).replace(/"/g, '\\"')}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },

    async fetchThreads() {
      this.isThreadsLoading = true;
      try {
        const response = await fetchUserThreadsAPI();
        this.threads = (response || []).map((t) => ({
          id: t.thread_id,
          version: t.version,
          title: t.title,
          createdAt: t.created_at,
          updatedAt: t.updated_at,
        })).sort((a, b) => {
          if (!a.updatedAt && !b.updatedAt) return 0;
          if (!a.updatedAt) return 1;
          if (!b.updatedAt) return -1;
          return new Date(b.updatedAt) - new Date(a.updatedAt);
        });
      } finally {
        this.isThreadsLoading = false;
      }
    },

    /**
     * Back on the page after visiting another one. A thread id in the URL wins
     * (e.g. a deep link); a bare /tracey returns to the chat that was open,
     * which may still be streaming.
     */
    syncWithRouteOnReturn() {
      if (this.$route.params.threadId) {
        this.restoreThreadFromRoute();
        return;
      }
      const id = this.activeThreadId;
      if (id != null && id !== 'pending') {
        this.$router.replace({ name: 'tracey-chat', params: { threadId: String(id) } }).catch(() => {});
      }
    },

    restoreThreadFromRoute() {
      const id = this.$route.params.threadId;
      if (!id) return;
      if (String(id) === String(this.activeThreadId)) return;
      const thread = this.threads.find((t) => String(t.id) === String(id))
        || { id, version: 'v2', title: 'Untitled' };
      this.selectThread(thread);
    },

    mapThreadMessages(response) {
      return (response || []).map((msg) =>
          msg.version === 'v1'
            ? {
              id: msg.id,
              version: 'v1',
              userInput: msg.user_input,
              response: msg.response,
              querystring: msg.querystring,
              createdAt: msg.created_at,
            }
            : {
              id: msg.id,
              version: 'v2',
              threadId: msg.thread_id,
              role: msg.role,
              content: msg.content,
              toolCalls: msg.tool_calls,
              fileAttachment: msg.file_attachment
                ? {
                  filename: msg.file_attachment.filename,
                  downloadUrl: msg.file_attachment.download_url,
                }
                : null,
              createdAt: msg.created_at,
            }
      );
    },

    async selectThread(thread) {
      this.isChatLoading = true;
      try {
        const response = await fetchMessagesForThreadAPI(thread.id);
        const msgs = this.mapThreadMessages(response);
        this.$set(this.messagesByThreadId, thread.id, msgs);
        this.activeThread = thread;
        this.activeThreadId = thread.id;
        this.awaitReplyIfPending(thread.id, msgs);
      } catch (e) {
        this.$set(this.messagesByThreadId, thread.id, []);
      } finally {
        this.isChatLoading = false;
      }
    },

    /**
     * If a thread ends in a query with no reply and nothing is streaming it in
     * this tab, the run is still finishing server-side (the page was left or
     * reloaded mid-answer). Poll its history -- which also makes the backend
     * recover messages from the agent -- until the reply appears.
     */
    awaitReplyIfPending(threadId, msgs) {
      const last = msgs[msgs.length - 1];
      const unanswered = last && last.version === 'v2' && last.role === 'user';
      if (!unanswered) {
        this.stopAwaitingReply(threadId);
        return;
      }
      if (this.streamingByThreadId[threadId] || this.submittingThreadIds[threadId]) return;
      if (this.replyPollTimers[threadId]) return;

      this.$set(this.awaitingReplyThreadIds, threadId, true);
      let attempts = 0;
      const timer = setInterval(async () => {
        attempts += 1;
        if (this.isComponentDestroyed || attempts > REPLY_POLL_MAX_ATTEMPTS) {
          this.stopAwaitingReply(threadId);
          return;
        }
        try {
          const latest = this.mapThreadMessages(await fetchMessagesForThreadAPI(threadId));
          const tail = latest[latest.length - 1];
          if (tail && tail.role !== 'user') {
            this.$set(this.messagesByThreadId, threadId, latest);
            this.stopAwaitingReply(threadId);
          }
        } catch (e) {
          /* transient; try again on the next tick */
        }
      }, REPLY_POLL_INTERVAL_MS);
      this.$set(this.replyPollTimers, threadId, timer);
    },

    stopAwaitingReply(threadId) {
      if (this.replyPollTimers[threadId]) clearInterval(this.replyPollTimers[threadId]);
      this.$delete(this.replyPollTimers, threadId);
      this.$delete(this.awaitingReplyThreadIds, threadId);
    },

    startNewChat() {
      this.inputText = '';
      this.activeThread = null;
      this.activeThreadId = null;
    },

    retryMessage(message) {
      const threadId = this.activeThreadId;
      const query = message && message.retryQuery;
      if (!threadId || !query) return;
      // Drop the failed error turn and the user message that triggered it, then
      // resend cleanly (submitMessage re-adds the user message).
      const list = this.messagesByThreadId[threadId];
      if (list) {
        const errIdx = list.findIndex((m) => m.id === message.id);
        if (errIdx !== -1) {
          list.splice(errIdx, 1);
          if (errIdx - 1 >= 0 && list[errIdx - 1] && list[errIdx - 1].role === 'user') {
            list.splice(errIdx - 1, 1);
          }
        }
      }
      if (message.retryVersion) this.traceyVersion = message.retryVersion;
      this.inputText = query;
      this.$nextTick(() => this.submitMessage());
    },

    async deleteThread(thread) {
      this.isThreadsLoading = true;
      try {
        await deleteTraceyThread(thread.id);
        this.$delete(this.messagesByThreadId, thread.id);
        if (thread.id === this.activeThreadId) this.startNewChat();
        await this.fetchThreads();
      } finally {
        this.isThreadsLoading = false;
      }
    },

    addMessageToThread(threadId, message) {
      const list = this.messagesByThreadId[threadId] ?? [];
      if (!this.messagesByThreadId[threadId]) {
        this.$set(this.messagesByThreadId, threadId, list);
      }
      list.push(message);
    },

    ensureThreadInList(threadId, title = 'Untitled') {
      const exists = this.threads.some((t) => t.id === threadId);
      if (!exists) {
        this.threads.unshift({
          id: threadId,
          version: 'v2',
          title,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
        this.threads.sort((a, b) => {
          const au = a.updatedAt ? new Date(a.updatedAt) : 0;
          const bu = b.updatedAt ? new Date(b.updatedAt) : 0;
          return bu - au;
        });
      }
    },

    abortStream() {
      Object.keys(this.streamAbortControllers).forEach((key) => {
        if (key !== 'pending') {
          stopTraceyThread(key).catch(() => {});
        }
        this.streamAbortControllers[key].abort();
      });
      this.streamAbortControllers = {};
    },

    stopGeneration() {
      const threadId = this.activeThreadId;
      if (!threadId) return;
      this.stopAwaitingReply(threadId);

      const controller = this.streamAbortControllers[threadId];
      if (controller) {
        controller.abort();
        this.$delete(this.streamAbortControllers, threadId);
      }

      if (threadId !== 'pending') {
        stopTraceyThread(threadId).catch((err) => {
          console.error('Stop API error:', err);
        });
      }

      const streamState = this.streamingByThreadId[threadId];
      if (streamState && streamState.content) {
        this.addMessageToThread(threadId, {
          id: crypto.randomUUID?.() || `msg-${Date.now()}`,
          version: 'v2',
          role: 'assistant',
          content: streamState.content,
          toolCalls: streamState.allToolCalls || [],
          createdAt: new Date().toISOString(),
        });
      }

      this.$delete(this.streamingByThreadId, threadId);
      this.$delete(this.submittingThreadIds, threadId);
    },

    async submitMessage() {
      if (this.isActiveThreadBusy) return;
      if (this.outOfCredits) return;
      const text = this.inputText?.trim();
      if (!text) return;

      const timestamp = new Date().toISOString();
      const targetThreadId = this.activeThreadId;
      const myKey = targetThreadId || 'pending';

      const userMessage = {
        id: `temp-${Date.now()}`,
        version: 'v2',
        role: 'user',
        content: text,
        toolCalls: [],
        createdAt: timestamp,
      };

      if (targetThreadId) {
        this.addMessageToThread(targetThreadId, userMessage);
      } else {
        this.$set(this.messagesByThreadId, 'pending', [userMessage]);
        this.activeThreadId = 'pending';
        this.activeThread = { id: 'pending', version: 'v2', title: 'New Chat' };
      }

      this.inputText = '';
      this.scrollToBottom(true);
      this.$set(this.submittingThreadIds, myKey, true);
      const controller = new AbortController();
      this.$set(this.streamAbortControllers, myKey, controller);

      let currentKey = myKey;
      try {
        const response = await submitTraceyQuery(text, targetThreadId || '', {
          signal: controller.signal,
          version: this.traceyVersion,
        });
        if (!response.ok) {
          const errBody = await response.text();
          const creditKind = this.handleCreditHttpError(response.status, errBody);
          if (creditKind) {
            // Credit block / in-flight lock: nothing was generated, so roll
            // back the optimistic user bubble and restore the input so the
            // user can retry (or see the out-of-credits overlay).
            if (targetThreadId) {
              const list = this.messagesByThreadId[targetThreadId];
              if (list && list[list.length - 1] === userMessage) list.pop();
            } else {
              this.$delete(this.messagesByThreadId, 'pending');
              this.activeThreadId = null;
              this.activeThread = null;
            }
            this.inputText = text;
            if (creditKind === 'query_already_running') {
              window.alert('A Tracey query is already running. Please wait for it to finish or stop it.');
            }
            return;
          }
          const err = new Error(`Query failed: ${response.status} ${response.statusText}`);
          err.status = response.status;
          err.body = errBody;
          throw err;
        }
        if (!response.body) throw new Error('No response body');

        this.$delete(this.submittingThreadIds, myKey);
        this.$set(this.streamingByThreadId, myKey, {
          // `thinking` collects the agent's preamble streamed BEFORE any tool call
          // (e.g. "I'll start by..."); `content` is the final answer streamed after
          // tools run. Kept separate so they render as distinct bubbles.
          thinking: '',
          content: '',
          pendingTools: [],
          allToolCalls: [],
        });

        if (!targetThreadId) {
          this.activeThreadId = 'pending';
          this.activeThread = { id: 'pending', version: 'v2', title: 'New Chat' };
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let streamThreadId = targetThreadId;

        while (true) {
          const { done, value } = await reader.read();
          if (this.isComponentDestroyed) break;
          if (value) buffer += decoder.decode(value, { stream: true });

          let newlineIdx;
          while ((newlineIdx = buffer.indexOf('\n')) !== -1) {
            const line = buffer.slice(0, newlineIdx);
            buffer = buffer.slice(newlineIdx + 1);
            if (!line.trim()) continue;
            try {
              const chunk = JSON.parse(line);
              if (chunk.type === 'balance') {
                this.applyCreditSnapshot(chunk);
                continue;
              }
              if (chunk.thread_id && chunk.thread_id !== streamThreadId) {
                streamThreadId = chunk.thread_id;
                if (myKey === 'pending') {
                  const state = this.streamingByThreadId['pending'];
                  this.$delete(this.streamingByThreadId, 'pending');
                  this.$set(this.streamingByThreadId, streamThreadId, state);
                  this.$set(this.streamAbortControllers, streamThreadId, this.streamAbortControllers['pending']);
                  this.$delete(this.streamAbortControllers, 'pending');
                  currentKey = streamThreadId;
                }
                this.ensureThreadInList(streamThreadId);
                if (!targetThreadId) {
                  const pending = this.messagesByThreadId['pending'] ?? [];
                  this.$delete(this.messagesByThreadId, 'pending');
                  this.$set(this.messagesByThreadId, streamThreadId, pending);
                  this.activeThreadId = streamThreadId;
                  this.activeThread = this.threads.find((t) => t.id === streamThreadId) || {
                    id: streamThreadId,
                    version: 'v2',
                    title: 'Untitled',
                  };
                }
              }
              const stateKey = currentKey;
              const state = this.streamingByThreadId[stateKey];
              if (state) {
                if (chunk.type === 'token') {
                  if ((state.allToolCalls || []).length === 0) {
                    state.thinking = (state.thinking || '') + (chunk.message || '');
                  } else {
                    state.content += chunk.message || '';
                  }
                  state.pendingTools = [];
                } else if (chunk.type === 'tool_calls') {
                  state.pendingTools = chunk.message || [];
                  state.allToolCalls = [...(state.allToolCalls || []), ...(chunk.message || [])];
                } else if (chunk.type === 'file') {
                  state.fileAttachment = {
                    filename: chunk.filename,
                    downloadUrl: chunk.download_url,
                  };
                }
              }
            } catch (e) {
              console.error('Parse chunk error:', line, e);
            }
          }

          if (done) {
            if (this.isComponentDestroyed) break;
            const remaining = decoder.decode();
            if (remaining) buffer += remaining;
            if (buffer.trim()) {
              try {
                const chunk = JSON.parse(buffer);
                if (chunk.type === 'balance') {
                  this.applyCreditSnapshot(chunk);
                } else {
                if (chunk.thread_id && chunk.thread_id !== streamThreadId) {
                  streamThreadId = chunk.thread_id;
                  if (myKey === 'pending') {
                    const state = this.streamingByThreadId['pending'];
                    this.$delete(this.streamingByThreadId, 'pending');
                    this.$set(this.streamingByThreadId, streamThreadId, state);
                    this.$set(this.streamAbortControllers, streamThreadId, this.streamAbortControllers['pending']);
                    this.$delete(this.streamAbortControllers, 'pending');
                    currentKey = streamThreadId;
                  }
                  this.ensureThreadInList(streamThreadId);
                  if (!targetThreadId) {
                    const pending = this.messagesByThreadId['pending'] ?? [];
                    this.$delete(this.messagesByThreadId, 'pending');
                    this.$set(this.messagesByThreadId, streamThreadId, pending);
                    this.activeThreadId = streamThreadId;
                    this.activeThread = this.threads.find((t) => t.id === streamThreadId) || {
                      id: streamThreadId,
                      version: 'v2',
                      title: 'Untitled',
                    };
                  }
                }
                const stateKey = currentKey;
                const state = this.streamingByThreadId[stateKey];
                if (state) {
                  if (chunk.type === 'token') {
                    if ((state.allToolCalls || []).length === 0) {
                      state.thinking = (state.thinking || '') + (chunk.message || '');
                    } else {
                      state.content += chunk.message || '';
                    }
                    state.pendingTools = [];
                  } else if (chunk.type === 'tool_calls') {
                    state.pendingTools = chunk.message || [];
                    state.allToolCalls = [...(state.allToolCalls || []), ...(chunk.message || [])];
                  } else if (chunk.type === 'file') {
                    state.fileAttachment = {
                      filename: chunk.filename,
                      downloadUrl: chunk.download_url,
                    };
                  }
                }
                }
              } catch (e) {
                console.error('Parse final chunk error:', buffer, e);
              }
            }

            const finalThreadId = streamThreadId || currentKey;
            const finalState = this.streamingByThreadId[finalThreadId];
            if (finalThreadId && finalState) {
              // If no tools ran, everything streamed is the answer (not "thinking").
              let thinking = finalState.thinking || '';
              let content = finalState.content || '';
              if (!content) {
                content = thinking;
                thinking = '';
              }
              const assistantMessage = {
                id: crypto.randomUUID?.() || `msg-${Date.now()}`,
                version: 'v2',
                role: 'assistant',
                thinking,
                content,
                toolCalls: finalState.allToolCalls || [],
                fileAttachment: finalState.fileAttachment || null,
                createdAt: timestamp,
              };
              this.addMessageToThread(finalThreadId, assistantMessage);
            }
            this.$delete(this.streamingByThreadId, finalThreadId);
            this.$delete(this.streamAbortControllers, finalThreadId);
            break;
          }
        }
        if (this.isComponentDestroyed) return;
        await this.fetchThreads();
      } catch (error) {
        if (this.isComponentDestroyed) return;
        if (error.name === 'AbortError') return;
        const errMsg = "Sorry, something went wrong generating that response.";
        const fallbackId = this.activeThreadId || myKey;
        const assistantMessage = {
          id: crypto.randomUUID?.() || `msg-${Date.now()}`,
          version: 'v2',
          role: 'assistant',
          content: errMsg,
          toolCalls: [],
          createdAt: timestamp,
          // Flag as an error turn so it renders distinctly with a Retry action,
          // carrying the query + agent needed to resend.
          isError: true,
          retryQuery: text,
          retryVersion: this.traceyVersion,
        };
        this.addMessageToThread(fallbackId, assistantMessage);
        if (fallbackId === 'pending') {
          this.$set(this.messagesByThreadId, 'pending', this.messagesByThreadId['pending'] || []);
        }
        console.error('Tracey query error:', error?.status ? `${error.status}: ${error.body || error.message}` : error);
      } finally {
        if (this.isComponentDestroyed) return;
        this.$delete(this.submittingThreadIds, myKey);
        this.$delete(this.streamingByThreadId, currentKey);
        this.$delete(this.streamAbortControllers, currentKey);
      }
    },

    isThreadBusy(threadId) {
      return !!(this.submittingThreadIds[threadId] || this.streamingByThreadId[threadId]);
    },
    isQueryDataPresent(message) {
      if (message?.toolCalls?.length) {
        return message.toolCalls.some((c) => QUERY_DATA_TOOLS.includes(c.name));
      }
      return !!message?.querystring;
    },

    getMatchingToolCalls(message) {
      if (!message?.toolCalls?.length) return [];
      return message.toolCalls.filter((c) => QUERY_DATA_TOOLS.includes(c.name));
    },

    viewQueryData(message) {
      if (message?.version === 'v1') {
        const query = this.queryStringToJson(message.querystring || '');
        query.viewIndex = 0;
        this.$router.push({ path: '/search', query });
        return;
      }
      if (message?.version === 'v2') {
        const matching = this.getMatchingToolCalls(message);
        if (matching.length >= 1) {
          this.matchingToolCalls = matching;
          this.toolCallModalMessage = message;
          this.showToolCallModal = true;
          return;
        }
        const toolCall = matching[0];
        if (toolCall) {
          const viewIndex = TOOL_TO_VIEW_INDEX[toolCall.name] ?? 0;
          const query = { viewIndex };
          for (const key in toolCall.args || {}) {
            if (key === 'auth_token' || key === 'size') continue;
            if (key === 'leaderboard_type') query.leaderboardIndex = this.inferLeaderboardIndex(toolCall.args[key]);
            else query[key] = toolCall.args[key];
          }
          this.$router.push({ path: '/search', query });
        }
      }
    },

    selectToolCallAndView(toolCall) {
      this.showToolCallModal = false;
      const msg = this.toolCallModalMessage;
      if (msg && toolCall) {
        const viewIndex = TOOL_TO_VIEW_INDEX[toolCall.name] ?? 0;
        const query = { viewIndex };
        for (const key in toolCall.args || {}) {
          if (key === 'auth_token' || key === 'size') continue;
          if (key === 'leaderboard_type') query.leaderboardIndex = this.inferLeaderboardIndex(toolCall.args[key]);
          else query[key] = toolCall.args[key];
        }
        this.$router.push({ path: '/search', query });
      }
      this.toolCallModalMessage = null;
      this.matchingToolCalls = [];
    },

    getToolCallDisplayName(name) {
      const map = {
        get_leaderboard: 'Leaderboard',
        search_tenders: 'Search Tenders',
        get_market_insights: 'Market Insights',
        get_search_summary: 'Search Summary',
      };
      return map[name] || name;
    },

    getToolCallDisplaySummary(toolCall) {
      const args = toolCall?.args;
      if (!args) return '';
      const skip = ['auth_token', 'size'];
      const parts = [];
      if (args.query) parts.push(`Query: ${args.query}`);
      if (args.leaderboard_type) parts.push(`Type: ${args.leaderboard_type}`);
      for (const key of Object.keys(args)) {
        if (skip.includes(key) || key === 'query' || key === 'leaderboard_type') continue;
        const v = args[key];
        if (v != null && v !== '') parts.push(`${key}: ${v}`);
      }
      return parts.join(' · ');
    },

    queryStringToJson(str) {
      if (str.startsWith('?')) str = str.slice(1);
      const params = new URLSearchParams(str);
      const result = {};
      params.forEach((v, k) => {
        if (result[k]) result[k] = Array.isArray(result[k]) ? [...result[k], v] : [result[k], v];
        else result[k] = v;
      });
      return result;
    },

    inferLeaderboardIndex(type) {
      const map = { publisher: 0, category: 1, supplier: 2 };
      return map[type];
    },

    togglePromptLibrary() {
      this.showPromptLibrary = !this.showPromptLibrary;
    },

    usePrompt(prompt) {
      this.inputText = prompt.prompt || '';
      this.showPromptLibrary = false;
      this.$nextTick(() => this.$refs.composer?.focusInput?.());
    },

    startTutorial() {
      // Ensure every step's target is on screen before the tour measures it.
      this.sidebarCollapsed = false;
      this.showPromptLibrary = false;
      this.$nextTick(() => {
        this.showTutorial = true;
      });
    },

    onTutorialStep({ key } = {}) {
      // The history panel lives in the (collapsible) sidebar, so make sure it is
      // expanded whenever the tour points at it.
      if (key === 'history' && this.sidebarCollapsed) {
        this.sidebarCollapsed = false;
      }
    },

    onTutorialFinish() {
      try {
        localStorage.setItem(TUTORIAL_SEEN_KEY, 'true');
      } catch (e) {
        /* storage unavailable — tour just runs again next time */
      }
    },

    maybeAutoStartTutorial() {
      let seen = false;
      try {
        seen = localStorage.getItem(TUTORIAL_SEEN_KEY) === 'true';
      } catch (e) {
        seen = false;
      }
      // Only auto-run for brand-new users on an empty chat, and never over a
      // small screen where the tour targets would be cramped.
      if (seen) return;
      if (typeof window !== 'undefined' && window.innerWidth < 768) return;
      if (this.displayMessages.length > 0) return;
      this.$nextTick(() => setTimeout(() => this.startTutorial(), 600));
    },
  },
};
</script>

<style scoped>
.ai-shell {
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: #fff;
  font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.ai-main {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  height: 100%;
  background: #fff;
}

.ai-main__topbar {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  height: 48px;
  padding: 0 12px 0 4px;
  border-bottom: 1px solid #ececf1;
  background: #fff;
  z-index: 2;
}

.ai-main__sidebar-toggle {
  flex-shrink: 0;
  color: #353740 !important;
}

.ai-main__title {
  flex: 1;
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #0d0d0d;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.ai-main__credits-group {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.ai-main__topup-btn {
  background: #ff1463 !important;
  color: #fff !important;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
  box-shadow: none !important;
}

.ai-main__usage-btn {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
  color: #12253a !important;
  border-color: #cdd5df !important;
}

.ai-main__tutorial-btn {
  flex-shrink: 0;
  margin-right: 6px;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
  color: #ff1463 !important;
  border-color: #ffc2d8 !important;
}

.ai-main__tutorial-btn .v-icon {
  color: #ff1463 !important;
}

.ai-main__topup-btn .v-icon {
  color: #fff !important;
}

.ai-main__credits {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 10px;
  border-radius: 13px;
  background: #f2f4f7;
  border: 1px solid #e4e7ec;
  color: #475467;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  cursor: default;
}

.ai-main__credits-icon {
  color: #667085 !important;
}

.ai-main__credits-agent {
  font-weight: 500;
  color: #98a2b3;
}

.ai-main__credits--ultra .ai-main__credits-icon {
  color: #ff1463 !important;
}

.ai-main__credits--sprint .ai-main__credits-icon {
  color: #0891b2 !important;
}

.ai-main__credits--low {
  background: #fff8eb;
  border-color: #fedf89;
  color: #b54708;
}

.ai-main__credits--low .ai-main__credits-icon {
  color: #dc6803 !important;
}

.ai-main__credits--low .ai-main__credits-agent {
  color: #dc6803;
}

.ai-main__credits--empty {
  background: #fef3f2;
  border-color: #fecdca;
  color: #b42318;
}

.ai-main__credits--empty .ai-main__credits-icon,
.ai-main__credits--empty .ai-main__credits-agent {
  color: #d92d20 !important;
}

.ai-main__body {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.ai-main__feed-wrap {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.ai-main__loading-skeletons {
  position: absolute;
  inset: 0;
  z-index: 2;
  background: #fff;
  padding: 16px 24px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ai-main__skeleton-turn {
  max-width: 52rem;
}

.ai-main__feed {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  width: 100%;
}

.ai-main__jump-bottom {
  position: absolute;
  left: 50%;
  bottom: 12px;
  transform: translateX(-50%);
  z-index: 3;
  background: #12253a !important;
  color: #fff !important;
  box-shadow: 0 3px 10px rgba(18, 37, 58, 0.28) !important;
}

/* Empty state: top-align the column and centre the hero with auto margins.
   justify-content: center pushes overflow off BOTH ends when the hero is
   taller than the pane (e.g. at 110%+ zoom), and the top part can't be
   scrolled to. Auto margins centre when there's room and collapse to 0 when
   there isn't, so the hero stays fully visible and scrollable. */
.ai-main__feed--empty {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 16px;
}

.tool-call-summary {
  white-space: normal !important;
  line-height: 1.4;
}

/* Topbar compresses as the viewport narrows (window resize or browser zoom):
   first the button labels go icon-only, then the agent names on the credit
   chips, so the title and controls never overflow. Buttons keep their
   title/aria-label tooltips when labels are hidden. */
@media (max-width: 1280px) {
  .ai-main__btn-label {
    display: none;
  }
  .ai-main__tutorial-btn ::v-deep .v-icon--left,
  .ai-main__topup-btn ::v-deep .v-icon--left,
  .ai-main__usage-btn ::v-deep .v-icon--left {
    margin-right: 0;
  }
  .ai-main__tutorial-btn,
  .ai-main__topup-btn,
  .ai-main__usage-btn {
    min-width: 0 !important;
    padding: 0 8px !important;
  }
}

@media (max-width: 900px) {
  .ai-main__credits-agent {
    display: none;
  }
  .ai-main__credits {
    padding: 0 8px;
  }
  .ai-main__credits-group {
    gap: 4px;
  }
}

@media (max-width: 768px) {
  .ai-shell {
    position: relative;
  }
}
</style>
